import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Table } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import { desglosePedido } from "../../../desglosePedido";
import { folioVendedor } from "../../../folioVendedor";

// Todo lo que salio de un prepedido dividido: cuanto se repartio y, por cada pedido
// generado, sus datos, en que va (PO, embarque, recepcion) y sus modelos con colores,
// tallas y proporciones. Los pedidos se traen aparte del listado para que salgan
// todos, aunque su fecha quede fuera del rango filtrado.
const headers = () => ({ Authorization: `Bearer: ${localStorage.getItem("app_token")}` });
const num = (n) => new Intl.NumberFormat("en-US").format(Number(n) || 0);
const dinero = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "MXN", maximumFractionDigits: 2 }).format(Number(n) || 0);
const limpio = (v) => (v && v !== "NA" ? v : "");
const vendedorDe = (p) => [p.colaboradores?.[0]?.nombre, limpio(p.colaboradores?.[0]?.apellido)].filter(Boolean).join(" ");

function Dato({ titulo, valor }) {
  return (
    <div style={{ minWidth: 92 }}>
      <div style={{ fontSize: 11, color: "#64748b" }}>{titulo}</div>
      <div style={{ fontWeight: 600 }}>{valor || "—"}</div>
    </div>
  );
}

function DetalleDivididoModal({ abierto, cerrar, prepedido }) {
  const [pedidos, setPedidos] = useState([]);
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    if (!abierto || !prepedido) return;
    let vigente = true;
    setCargando(true);
    setPedidos([]);
    (async () => {
      try {
        const lista = (await axios.get(`${process.env.REACT_APP_URL_PEDIDOS}Divididos/${prepedido._id}`, { headers: headers() })).data || [];
        const [modelos, avance] = await Promise.all([
          Promise.all(lista.map((p) => desglosePedido(p._id, { conFoto: false }))),
          // el avance solo existe para pedidos abiertos; si falla, el detalle sale sin el
          axios.get(`${process.env.REACT_APP_URL_SEGUIMIENTOS}Pedidos`, { headers: headers() }).then((r) => r.data || []).catch(() => []),
        ]);
        if (!vigente) return;
        setPedidos(lista.map((p, i) => ({ ...p, modelos: modelos[i], avance: avance.find((a) => a._id === p._id) })));
      } catch (e) {
        Swal.fire({ icon: "error", title: "No se pudo traer el detalle", text: e.response?.data?.message || e.message });
      }
      if (vigente) setCargando(false);
    })();
    return () => { vigente = false; };
  }, [abierto, prepedido]);

  const repartido = pedidos.reduce((s, p) => s + (Number(p.total_cantidad) || 0), 0);
  const surtido = pedidos.reduce((s, p) => s + (Number(p.piezasEntregadas) || 0), 0);
  const total = Number(prepedido?.total_cantidad) || 0;

  return (
    <Modal size="xl" isOpen={abierto} toggle={cerrar}>
      <ModalHeader toggle={cerrar}>Prepedido {prepedido?.numero} · {prepedido?.clientes}</ModalHeader>
      <ModalBody>
        {cargando ? <p style={{ color: "#64748b" }}>Cargando el detalle...</p> : !pedidos.length ? (
          <p style={{ color: "#94a3b8" }}>No se encontraron pedidos ligados a este prepedido.</p>
        ) : (
          <>
            <div style={{ display: "flex", gap: 22, flexWrap: "wrap", marginBottom: 14 }}>
              <Dato titulo="Piezas del prepedido" valor={num(total)} />
              <Dato titulo="Repartidas" valor={num(repartido)} />
              <Dato titulo="Sin repartir" valor={num(Math.max(0, total - repartido))} />
              <Dato titulo="Surtidas" valor={num(surtido)} />
              <Dato titulo="Por surtir" valor={num(repartido - surtido)} />
              <Dato titulo="Total con impuestos" valor={dinero(pedidos.reduce((s, p) => s + (Number(p.total_general) || 0), 0))} />
            </div>
            {pedidos.map((p) => (
              <section key={p._id} style={{ border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 12px", marginBottom: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
                  <h5 style={{ margin: 0 }}>
                    Pedido {p.idPedido}
                    <span style={{ color: "#64748b", fontSize: 13, fontWeight: 400 }}> · {folioVendedor(vendedorDe(p), p.consecutivoVendedor, p.divisionNumero)} · {p.status}</span>
                  </h5>
                  <a href={`/SeguimientoGeneral?pedido=${p.idPedido}`} style={{ fontSize: 12 }}>Ver seguimiento</a>
                </div>
                <div style={{ display: "flex", gap: 22, flexWrap: "wrap", marginBottom: 8, fontSize: 13 }}>
                  <Dato titulo="Fecha" valor={p.fecha} />
                  <Dato titulo="Cancelación" valor={limpio(p.fechaCancelacion)} />
                  <Dato titulo="OC del cliente" valor={limpio(p.ordenCompraCliente)} />
                  <Dato titulo="Vendedor" valor={vendedorDe(p)} />
                  <Dato titulo="Piezas" valor={num(p.total_cantidad)} />
                  <Dato titulo="Surtidas" valor={num(p.piezasEntregadas)} />
                  <Dato titulo="Total con impuestos" valor={dinero(p.total_general)} />
                  {p.avance ? (
                    <>
                      <Dato titulo="En PO" valor={`${num(p.avance.enOC)}${p.avance.pos ? ` · ${p.avance.pos}` : ""}`} />
                      <Dato titulo="Embarcadas" valor={num(p.avance.embarcado)} />
                      <Dato titulo="Recibidas" valor={num(p.avance.recibido)} />
                      <Dato titulo="Estado" valor={p.avance.estado} />
                    </>
                  ) : null}
                </div>
                {limpio(p.observaciones) ? <div style={{ fontSize: 12, color: "#64748b", marginBottom: 8 }}>{p.observaciones}</div> : null}
                <div style={{ overflowX: "auto" }}>
                  <Table size="sm" bordered style={{ marginBottom: 0 }}>
                    <thead>
                      <tr>
                        <th>#</th><th>Modelo</th><th>Colores</th><th>Tallas</th>
                        <th style={{ textAlign: "right" }}>Piezas</th><th style={{ textAlign: "right" }}>Surtido</th><th style={{ textAlign: "right" }}>Por surtir</th>
                        <th style={{ textAlign: "right" }}>Precio</th><th style={{ textAlign: "right" }}>Importe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {p.modelos.map((m) => (
                        <tr key={`${m.articulos}|${m.renglon}`}>
                          <td>{m.renglon}</td>
                          <td style={{ whiteSpace: "nowrap" }}><b>{m.codigo}</b> {m.nombre}</td>
                          <td style={{ fontSize: 12 }}>{m.desgloseColores}{m.colores.length > 1 ? <div style={{ color: "#64748b" }}>Proporción {m.proporcionColores}</div> : null}</td>
                          <td style={{ fontSize: 12 }}>{m.desgloseTallas}{m.tallas.length > 1 ? <div style={{ color: "#64748b" }}>Proporción {m.proporcionTallas}</div> : null}</td>
                          <td style={{ textAlign: "right" }}>{num(m.piezas)}</td>
                          <td style={{ textAlign: "right" }}>{num(m.surtido)}</td>
                          <td style={{ textAlign: "right" }}>{num(m.pendiente)}</td>
                          <td style={{ textAlign: "right" }}>{dinero(m.precio)}</td>
                          <td style={{ textAlign: "right" }}>{dinero(m.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </section>
            ))}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={cerrar}>Cerrar</Button>
      </ModalFooter>
    </Modal>
  );
}

export default DetalleDivididoModal;
